import { Component, EventEmitter, Input, OnChanges, SimpleChanges, Output, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  createPropertyPair,
  PropertyField,
  PropertyFieldOptionGroup,
  PropertyPair
} from '../../../../shared/components/property-panel/property-panel.model';
import { ReportsService } from '../../../reports.service';
import { GlobalFilterColumnOption, GlobalFilterSelection } from '../global-filter-modal/global-filter-modal.component';
import { resolveFilterValueColumnKey } from '../select-datasource-modal/select-datasource-modal.model';

export type FilterControlType = 'hierarchal' | 'hierarchal-delimiter' | 'single-select' | 'multiselect';
const FILTER_CONTROL_TYPES: FilterControlType[] = ['hierarchal', 'hierarchal-delimiter', 'single-select', 'multiselect'];

export interface FilterProperties {
  defaultSelection: string[];
  visibility: boolean;
  searchType: 'Equal' | 'Like';
  width: number;
}

// `columns` holds one label/value column pair per hierarchy level (a single pair for non-hierarchal types).
// `label` is the column shown to the user and `value` the column sent as the filter's id/query key.
export interface FilterRow {
  id: string;
  name: string;
  type: FilterControlType | '';
  columns: PropertyPair[];
  properties: FilterProperties;
}

// Multiple label/value pairs (hierarchy levels) are only offered for the Hierarchal control type.
export function allowsMultipleColumns(row: FilterRow): boolean {
  return row.type === 'hierarchal';
}

interface DataColumnOption {
  key: string;
  // Alias for display; `key` and `columnName` retain the actual datasource identifiers.
  displayName: string;
  type?: string;
  isPrimaryKey?: boolean;
  tableName: string;
  columnName: string;
  datasetId?: string;
}

interface DataColumnGroup {
  group: string;
  columns: DataColumnOption[];
}

function getColumnDisplayName(column: { alias?: unknown; name?: unknown; key?: unknown }): string {
  return String(column?.alias ?? '').trim() ||
    String(column?.name ?? '').trim() ||
    String(column?.key ?? '').trim();
}

function createDefaultProperties(): FilterProperties {
  return { defaultSelection: ['All'], visibility: true, searchType: 'Like', width: 3 };
}

// Every label/value pair needs both columns picked before the filter can be saved.
export function hasCompleteColumns(row: FilterRow): boolean {
  return Array.isArray(row?.columns) && row.columns.length > 0 && row.columns.every(pair =>
    typeof pair?.label === 'string' && !!pair.label.trim() &&
    typeof pair?.value === 'string' && !!pair.value.trim()
  );
}

export function isFilterRowValid(row: FilterRow): boolean {
  return typeof row?.name === 'string' && !!row.name.trim() &&
    FILTER_CONTROL_TYPES.some(type => type === row.type) &&
    (row.type !== 'hierarchal' || (Array.isArray(row.columns) && row.columns.length >= 2)) &&
    hasCompleteColumns(row);
}

export function hasDuplicateFilterNames(rows: FilterRow[]): boolean {
  const seen = new Set<string>();
  for (const row of rows) {
    const key = typeof row?.name === 'string' ? row.name.trim().toLowerCase() : '';
    if (!key) { continue; }
    if (seen.has(key)) { return true; }
    seen.add(key);
  }
  return false;
}

/**
 * "Visual Editor" tab of the Add filter popup - a reorderable table of filter rows (name, control
 * type + per-row "manage properties" panel, where the label/value data columns are chosen), matching
 * the required designer UI.
 */
@Component({
  selector: 'app-add-filter',
  standalone: false,
  templateUrl: './add-filter.component.html',
  styleUrl: './add-filter.component.scss'
})
export class AddFilterComponent implements OnChanges {
  @Input() draft: FilterRow[] | null = null;
  @Output() draftChange = new EventEmitter<FilterRow[]>();
  // Report's datasource config (object, or JSON string) - { datasets: [{ id, name, table_name, columns: [...] }] }.
  @Input() selectedDataSource: any = null;
  @Input() globalFilterSelections: GlobalFilterSelection[] = [];
  @Output() globalFilterSelectionsChange = new EventEmitter<GlobalFilterSelection[]>();
  @Input() tableFilterSelections: GlobalFilterSelection[] = [];
  @Output() tableFilterSelectionsChange = new EventEmitter<GlobalFilterSelection[]>();

  constructor(private reportsService: ReportsService) {}

  readonly controlTypeOptions: Array<{ value: FilterControlType; label: string }> = [
    { value: 'hierarchal', label: 'Hierarchal' },
    { value: 'hierarchal-delimiter', label: 'Hierarchal-delimiter' },
    { value: 'single-select', label: 'Single Select' },
    { value: 'multiselect', label: 'Multiselect' }
  ];

  readonly defaultSelectionOptions = ['All', 'None'];
  readonly searchTypeOptions = [
    { value: 'Equal', label: 'Equal' },
    { value: 'Like', label: 'Like' }
  ];

  rows: FilterRow[] = [];
  columnGroups: DataColumnGroup[] = [];
  private columnNamesByKey = new Map<string, string>();
  // Cached (not rebuilt per property-field build) so the panel's dropdown options keep a stable identity.
  columnOptionGroups: PropertyFieldOptionGroup[] = [];
  activePropertyRowId: string | null = null;

  private idCounter = 0;
  private readonly mainColumnSelectionCache = new WeakMap<FilterRow, string[]>();
  // Data column picker UI state - keyed by row id so each row's panel keeps its own search text /
  // collapsed groups without leaking into other rows.
  dataColumnSearchTerm: { [rowId: string]: string } = {};
  private readonly collapsedDataColumnGroups = new Set<string>();
  private readonly filteredColumnGroupsCache = new Map<string, { term: string; groups: DataColumnGroup[] }>();
  // `draftChange` round-trips through the parent back into `[draft]` - ignore that echo so we don't
  // replace `rows` (and break drag reordering / open property panel lookups) on every keystroke.
  private suppressNextDraftSync = false;
  defaultSelectionValuesLoading = false;
  private defaultSelectionApiValues: string[] = [];
  // Lets a slow default-selection response be discarded once the label columns (or open row) have moved on.
  private defaultSelectionRequestId = 0;

  globalFilterConditions: GlobalFilterSelection[] = [];
  // Cached (not a getter) - a new array identity every CD cycle made the column mat-select's *ngFor
  // destroy/recreate its options every check and froze the page, same as the propertyFields bug.
  globalFilterColumnOptions: GlobalFilterColumnOption[] = [];
  @ViewChild('globalFilterModal') private globalFilterModal: any;
  @ViewChild('globalFilterModalBody') private globalFilterModalBody: any;

  onOpenGlobalFilter(): void {
    this.globalFilterModalBody?.resetState(this.globalFilterConditions);
    this.globalFilterModal?.show();
  }

  onGlobalFilterCancelled(): void {
    this.globalFilterModal?.hide();
  }

  onGlobalFilterSaved(selections: GlobalFilterSelection[]): void {
    this.globalFilterConditions = selections.map(condition => ({
      ...condition,
      selectedValues: [...condition.selectedValues]
    }));
    this.globalFilterSelectionsChange.emit(this.globalFilterConditions);
    this.globalFilterModal?.hide();
  }

  tableFilterConditions: GlobalFilterSelection[] = [];
  @ViewChild('tableFilterModal') private tableFilterModal: any;
  @ViewChild('tableFilterModalBody') private tableFilterModalBody: any;

  onOpenTableFilter(): void {
    this.tableFilterModalBody?.resetState(this.tableFilterConditions);
    this.tableFilterModal?.show();
  }

  onTableFilterCancelled(): void {
    this.tableFilterModal?.hide();
  }

  onTableFilterSaved(selections: GlobalFilterSelection[]): void {
    this.tableFilterConditions = selections.map(condition => ({
      ...condition,
      selectedValues: [...condition.selectedValues]
    }));
    this.tableFilterSelectionsChange.emit(this.tableFilterConditions);
    this.tableFilterModal?.hide();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['draft']) {
      if (this.suppressNextDraftSync) {
        this.suppressNextDraftSync = false;
      } else {
        this.rows = this.draft?.length ? this.draft.map(row => this.cloneRow(row)) : [this.createEmptyRow()];
      }
    }
    if (changes['selectedDataSource']) {
      this.columnGroups = this.buildColumnGroups();
      this.filteredColumnGroupsCache.clear();
      this.columnNamesByKey = new Map(
        this.columnGroups.flatMap(group => group.columns.map(column => [column.key, column.displayName] as const))
      );
      this.columnOptionGroups = this.columnGroups.map(group => ({
        label: group.group,
        options: group.columns.map(col => ({ value: col.key, label: col.displayName }))
      }));
      this.globalFilterColumnOptions = this.columnGroups.flatMap(group => group.columns.map(col => ({
        key: col.key, label: col.displayName, tableName: col.tableName, columnName: col.columnName,
        datasetId: col.datasetId, tableLabel: col.tableName || group.group
      })));
      if (this.activeRow) {
        this.propertyFields = this.buildPropertyFields(this.activeRow);
      }
    }
    if (changes['globalFilterSelections']) {
      this.globalFilterConditions = (this.globalFilterSelections || []).map(condition => ({
        ...condition,
        selectedValues: [...condition.selectedValues]
      }));
    }
    if (changes['tableFilterSelections']) {
      this.tableFilterConditions = (this.tableFilterSelections || []).map(condition => ({
        ...condition,
        selectedValues: [...condition.selectedValues]
      }));
    }
  }

  get activeRow(): FilterRow | undefined {
    return this.rows.find(row => row.id === this.activePropertyRowId);
  }

  // Cached (not a getter) - rebuilding this array/its nested option arrays on every CD cycle made the
  // property panel's *ngFor destroy/recreate its mat-select every check and froze the page.
  propertyFields: PropertyField[] = [];

  private buildPropertyFields(row: FilterRow): PropertyField[] {
    const isHierarchy = allowsMultipleColumns(row);
    const defaultSelectionValues = [...new Set([...this.defaultSelectionOptions, ...this.defaultSelectionApiValues])];
    return [
      {
        key: 'columns',
        label: isHierarchy ? 'Data columns (drag to set hierarchy order)' : 'Data columns',
        type: 'pair-list',
        groups: this.columnOptionGroups,
        labelHeading: 'Label',
        valueHeading: 'Value',
        addLabel: 'Add more column',
        addable: isHierarchy,
        mirrorSelection: true,
        reorderable: isHierarchy && row.columns.length > 1
      },
      {
        key: 'defaultSelection',
        label: this.defaultSelectionValuesLoading ? 'Default selection (loading values...)' : 'Default selection',
        type: 'select', multiple: false,
        options: defaultSelectionValues.map(option => ({ value: option, label: option }))
      },
      { key: 'visibility', label: 'Visibility', type: 'toggle', inline: true },
      { key: 'searchType', label: 'Search', type: 'radio-toggle', options: this.searchTypeOptions, inline: true },
      { key: 'width', label: 'Width', type: 'slider', min: 3, max: 12, color: '#013b71' }
    ];
  }

  get activePropertyValues(): { [key: string]: any } {
    const row = this.activeRow;
    return row ? {
      ...row.properties,
      defaultSelection: row.properties.defaultSelection[0] ?? null,
      columns: row.columns
    } : {};
  }

  get summaryText(): string {
    const count = this.rows.length;
    return `${count} filter${count === 1 ? '' : 's'}`;
  }

  trackByRowId(_index: number, row: FilterRow): string {
    // Recreate the row when its type changes so the data-column mat-select is initialized
    // with the correct single- or multiple-selection mode.
    return `${row.id}:${row.type}`;
  }

  isDuplicateName(row: FilterRow): boolean {
    const name = row.name.trim().toLowerCase();
    if (!name) { return false; }
    return this.rows.some(other => other !== row && other.name.trim().toLowerCase() === name);
  }

  // Template-facing wrapper around the exported `hasCompleteColumns()` helper - flags rows whose
  // label/value columns (now chosen in "Manage properties") are still missing.
  needsColumns(row: FilterRow): boolean {
    return !hasCompleteColumns(row);
  }

  addRow(): void {
    this.rows = [...this.rows, this.createEmptyRow()];
    this.onFieldChange();
  }

  removeRow(row: FilterRow): void {
    this.rows = this.rows.filter(r => r !== row);
    if (this.activePropertyRowId === row.id) { this.activePropertyRowId = null; }
    delete this.dataColumnSearchTerm[row.id];
    this.filteredColumnGroupsCache.delete(row.id);
    this.onFieldChange();
  }

  onDrop(event: CdkDragDrop<FilterRow[]>): void {
    moveItemInArray(this.rows, event.previousIndex, event.currentIndex);
    this.onFieldChange();
  }

  // Switching away from Hierarchal trims back down to a single label/value pair.
  onTypeChange(row: FilterRow): void {
    if (!allowsMultipleColumns(row) && row.columns.length > 1) {
      this.applyColumns(row, row.columns.slice(0, 1));
    }
    this.onFieldChange();
  }

  getMainColumnSelection(row: FilterRow): string | string[] | null {
    if (allowsMultipleColumns(row)) {
      let selectedColumns = this.mainColumnSelectionCache.get(row);
      if (!selectedColumns) {
        selectedColumns = row.columns.map(pair => pair.label).filter((key): key is string => !!key);
        this.mainColumnSelectionCache.set(row, selectedColumns);
      }
      return selectedColumns;
    }
    return row.columns.find(pair => !!pair.label)?.label || null;
  }

  getMainColumnLabel(row: FilterRow): string {
    const selectedColumns = row.columns
      .map(pair => pair.label)
      .filter((key): key is string => !!key)
      .map(key => this.columnNamesByKey.get(key) || key);
    return selectedColumns.join(' / ');
  }

  onMainColumnSelectionChange(row: FilterRow, selection: string | string[] | null): void {
    const selectedKeys = (Array.isArray(selection) ? selection : selection ? [selection] : []);
    const selectedColumns = allowsMultipleColumns(row) ? selectedKeys : selectedKeys.slice(0, 1);
    const existingPairs = new Map(
      row.columns.filter(pair => pair.label).map(pair => [pair.label!, pair])
    );
    const unassignedPairs = row.columns.filter(pair => !pair.label);
    const usedPairs = new Set<PropertyPair>();
    const columns = selectedColumns.length
      ? selectedColumns.map(key => {
        const exactPair = existingPairs.get(key);
        const pair = exactPair && !usedPairs.has(exactPair)
          ? exactPair
          : unassignedPairs.shift() || row.columns.find(existing => !usedPairs.has(existing)) || createPropertyPair();
        usedPairs.add(pair);
        return pair.label === key ? pair : { ...pair, label: key, value: this.resolveValueColumnKey(key) };
      })
      : [createPropertyPair()];
    this.applyColumns(row, columns);
    this.onFieldChange();
  }

  // Hierarchal columns are picked via our own checkbox markup (not native mat-option selection -
  // see the template comment) so the toggle is always derived from `row.columns`, the single source
  // of truth, rather than from mat-select's own (getter-array-driven) selection model.
  toggleHierarchalColumn(row: FilterRow, key: string): void {
    const current = this.getMainColumnSelection(row);
    const currentKeys = Array.isArray(current) ? current : [];
    const next = currentKeys.includes(key)
      ? currentKeys.filter(existingKey => existingKey !== key)
      : [...currentKeys, key];
    this.onMainColumnSelectionChange(row, next);
  }

  // Panel is reset (search cleared) each time it closes so the next open starts from the full list.
  onDataColumnPanelOpenedChange(row: FilterRow, opened: boolean): void {
    if (!opened) {
      delete this.dataColumnSearchTerm[row.id];
      this.filteredColumnGroupsCache.delete(row.id);
    }
  }

  onDataColumnSearchChange(row: FilterRow, term: string): void {
    this.dataColumnSearchTerm[row.id] = term;
    this.cacheFilteredColumnGroups(row.id, term);
  }

  isColumnSelected(row: FilterRow, key: string): boolean {
    const selection = this.getMainColumnSelection(row);
    return Array.isArray(selection) ? selection.includes(key) : selection === key;
  }

  getSelectedColumnCount(row: FilterRow): number {
    const selection = this.getMainColumnSelection(row);
    return Array.isArray(selection) ? selection.length : (selection ? 1 : 0);
  }

  getSelectedColumnCountLabel(row: FilterRow): string {
    const count = this.getSelectedColumnCount(row);
    return count === 1 ? '1 selected column' : `${count} selected columns`;
  }

  // "Type • Unique" style subtitle shown under each column name in the picker, matching the
  // design's "application_id • Text • Unique" format.
  getColumnMeta(column: DataColumnOption): string {
    const parts: string[] = [];
    if (column.type) {
      parts.push(column.type.charAt(0).toUpperCase() + column.type.slice(1).toLowerCase());
    }
    if (column.isPrimaryKey) {
      parts.push('Unique');
    }
    return parts.join(' • ');
  }

  // Groups + their columns filtered by the row's current search text (name match, case-insensitive).
  // Groups left with no matching columns are dropped entirely.
  getFilteredColumnGroups(row: FilterRow): DataColumnGroup[] {
    const term = (this.dataColumnSearchTerm[row.id] || '').trim().toLowerCase();
    if (!term) { return this.columnGroups; }
    const cached = this.filteredColumnGroupsCache.get(row.id);
    if (cached?.term === term) { return cached.groups; }
    return this.cacheFilteredColumnGroups(row.id, term);
  }

  trackByColumnGroup(index: number, group: DataColumnGroup): string {
    return `${index}:${group.group}`;
  }

  trackByDataColumnOption(_index: number, column: DataColumnOption): string {
    return column.key;
  }

  private cacheFilteredColumnGroups(rowId: string, rawTerm: string): DataColumnGroup[] {
    const term = rawTerm.trim().toLowerCase();
    const groups = term
      ? this.columnGroups
      .map(group => ({
        group: group.group,
        columns: group.columns.filter(column =>
          column.displayName.toLowerCase().includes(term) || column.columnName.toLowerCase().includes(term))
      }))
      .filter(group => group.columns.length > 0)
      : this.columnGroups;
    this.filteredColumnGroupsCache.set(rowId, { term, groups });
    return groups;
  }

  isDataColumnGroupCollapsed(row: FilterRow, group: DataColumnGroup): boolean {
    return this.collapsedDataColumnGroups.has(`${row.id}:${group.group}`);
  }

  toggleDataColumnGroup(row: FilterRow, group: DataColumnGroup): void {
    const key = `${row.id}:${group.group}`;
    if (this.collapsedDataColumnGroups.has(key)) {
      this.collapsedDataColumnGroups.delete(key);
    } else {
      this.collapsedDataColumnGroups.add(key);
    }
  }

  openProperties(row: FilterRow): void {
    this.activePropertyRowId = row.id;
    this.defaultSelectionApiValues = [];
    this.propertyFields = this.buildPropertyFields(row);
    this.fetchDefaultSelectionValues(row);
  }

  closeProperties(): void {
    this.activePropertyRowId = null;
    this.propertyFields = [];
    this.defaultSelectionRequestId++;
    this.defaultSelectionValuesLoading = false;
  }

  onPropertyValueChange(event: { key: string; value: any }): void {
    const row = this.activeRow;
    if (!row) { return; }
    if (event.key === 'columns') {
      this.applyColumns(row, this.resolveChangedLabelValues(row.columns, event.value));
    } else if (event.key === 'defaultSelection') {
      const value = Array.isArray(event.value) ? event.value[0] : event.value;
      row.properties.defaultSelection = value == null || value === '' ? [] : [String(value)];
    } else {
      (row.properties as any)[event.key] = event.value;
    }
    this.onFieldChange();
  }

  // Resolves the Value key to pair with a selected Label column: the label column's filterColumn
  // (parsed from business_rules) names the sibling column to use, falling back to the label itself.
  private resolveValueColumnKey(labelKey: string): string {
    return resolveFilterValueColumnKey(labelKey, this.selectedDataSource);
  }

  // The side "manage properties" panel's Label/Value pickers are generic (it just mirrors Value into
  // an empty slot when either side is picked, with no notion of filterColumn). Whenever a pair's Label
  // has just changed AND its Value still looks like a plain mirror/leftover (not something the user
  // independently picked in the Value dropdown) - either freshly mirrored to equal the new label, or
  // simply carried over unchanged from before (PropertyPanelComponent only touches Value on a Label
  // change when mirroring a previously-empty slot) - re-derive Value the same way the main data-column
  // list does, so the side panel and the main list behave consistently. A Value the user picked
  // directly (different from both the label and the prior value) is left untouched.
  private resolveChangedLabelValues(previous: PropertyPair[], next: PropertyPair[]): PropertyPair[] {
    const previousById = new Map(previous.map(pair => [pair.id, pair]));
    return next.map(pair => {
      const before = previousById.get(pair.id);
      const labelChanged = !!pair.label && pair.label !== before?.label;
      const valueLooksDerived = pair.value === pair.label || pair.value === before?.value;
      if (labelChanged && valueLooksDerived) {
        return { ...pair, value: this.resolveValueColumnKey(pair.label!) };
      }
      return pair;
    });
  }

  onFieldChange(): void {
    // Keep the open panel's fields (multiple-flag, hierarchy reorder/add controls) in sync if the row's Type just changed.
    if (this.activeRow) {
      this.propertyFields = this.buildPropertyFields(this.activeRow);
    }
    this.suppressNextDraftSync = true;
    this.draftChange.emit(this.rows.map(row => this.cloneRow(row)));
  }

  // Default selection values come from the label columns, so changing which columns are used
  // invalidates both the offered values and any previously chosen default. Re-ordering the same
  // columns (hierarchy drag) keeps them.
  private applyColumns(row: FilterRow, next: PropertyPair[]): void {
    const labelsChanged = this.labelSignature(row.columns) !== this.labelSignature(next);
    row.columns = [...next];
    this.mainColumnSelectionCache.set(
      row,
      row.columns.map(pair => pair.label).filter((key): key is string => !!key)
    );
    if (!labelsChanged) { return; }
    row.properties.defaultSelection = ['All'];
    if (this.activeRow === row) {
      this.defaultSelectionApiValues = [];
      this.fetchDefaultSelectionValues(row);
    }
  }

  private labelSignature(columns: PropertyPair[]): string {
    return columns.map(pair => pair.label).filter(Boolean).sort().join('|');
  }

  private cloneRow(row: FilterRow): FilterRow {
    const columns = row.columns?.length ? row.columns.map(pair => ({ ...pair })) : [createPropertyPair()];
    return { ...row, columns, properties: { ...row.properties } };
  }

  private createEmptyRow(): FilterRow {
    return {
      id: `filter-${Date.now()}-${this.idCounter++}`,
      name: '',
      type: '',
      columns: [createPropertyPair()],
      properties: createDefaultProperties()
    };
  }

  private findColumnOption(key: string): DataColumnOption | undefined {
    for (const group of this.columnGroups) {
      const found = group.columns.find(col => col.key === key);
      if (found) { return found; }
    }
    return undefined;
  }

  // Populates `defaultSelectionApiValues` with the real distinct values for the row's label column(s)
  // so "Default selection" can offer them alongside the fixed All/None options.
  private fetchDefaultSelectionValues(row: FilterRow): void {
    const requestId = ++this.defaultSelectionRequestId;
    const columns = row.columns
      .map(pair => (pair.label ? this.findColumnOption(pair.label) : undefined))
      .filter((col): col is DataColumnOption => !!col);
    if (!columns.length) {
      this.defaultSelectionValuesLoading = false;
      return;
    }
    this.defaultSelectionValuesLoading = true;
    this.reportsService.getValuesByColumnNames({
      tableName: columns[0].tableName,
      columnNames: columns.map(col => col.columnName),
      distinct: true
    }).subscribe({
      next: (response: any) => {
        if (requestId !== this.defaultSelectionRequestId) { return; }
        this.defaultSelectionValuesLoading = false;
        this.defaultSelectionApiValues = Array.isArray(response) ? response : (response?.values || []);
        if (this.activeRow === row) {
          this.propertyFields = this.buildPropertyFields(row);
        }
      },
      error: () => {
        if (requestId !== this.defaultSelectionRequestId) { return; }
        this.defaultSelectionValuesLoading = false;
        this.defaultSelectionApiValues = [];
        if (this.activeRow === row) {
          this.propertyFields = this.buildPropertyFields(row);
        }
      }
    });
  }

  // One section per dataset the user configured (not grouped by column type) - each dataset's already
  // user-selected columns (from `selectedDataSource`) become that section's list.
  private buildColumnGroups(): DataColumnGroup[] {
    let parsed: any = this.selectedDataSource;
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { parsed = null; }
    }
    const datasets: any[] = parsed?.datasets || [];
    return datasets.map(dataset => ({
      group: dataset.name || dataset.table_name || 'Other',
      columns: (dataset.columns || []).map((col: any) => ({
        key: `${dataset.id}.${col.key}`,
        displayName: getColumnDisplayName(col),
        type: col.type,
        isPrimaryKey: !!col.is_primary_key,
        tableName: dataset.table_name,
        columnName: col.key,
        datasetId: dataset.id
      }))
    }));
  }
}
